import {useEffect, useRef} from 'react';
import {useSelector} from 'react-redux';
import Utils from '../utils/utils';
import {urlSearchParams} from '../baseUrls';
import {
  AI_CALL_FROM_QUERY_VALUE,
  markAiCallBrowserSession,
  recordAiCallTabClosed,
  withdrawAiCallTabClosedIfRefreshed,
} from '../utils/aiCallSession';

/**
 * When opened via ?fromAiCall=1 (AI call socket → new tab), marks this tab as an AI-call
 * browser session and, when it goes away, asks the other tabs to end the call and set the agent
 * Available again (see `recordAiCallTabClosed`).
 *
 * Nothing is done from here directly: `pagehide` fires the same way for a refresh as for a close,
 * and a refresh must leave both the call and the agent's availability alone. The request is
 * withdrawn as soon as this tab loads again, or comes back out of the back/forward cache.
 */
export function useAiCallSession() {
  const userId = useSelector((state) => state.user?.id);
  const openCallId = useSelector((state) => state.user?.aiOpenCall?.id);

  // Read at pagehide, so they must not be effect dependencies: re-running the effect would
  // re-register the listeners and, worse, re-run the refresh check below.
  const userIdRef = useRef(userId);
  const openCallIdRef = useRef(openCallId);
  userIdRef.current = userId;
  openCallIdRef.current = openCallId;

  useEffect(() => {
    const fromAiCall = Utils.getUrlParameter(urlSearchParams.fromAiCall);
    if (fromAiCall !== AI_CALL_FROM_QUERY_VALUE) {
      return undefined;
    }

    withdrawAiCallTabClosedIfRefreshed();
    markAiCallBrowserSession();

    const onPageHide = () => {
      recordAiCallTabClosed({
        userId: userIdRef.current,
        callId: openCallIdRef.current,
      });
    };

    // A page restored from the back/forward cache never re-runs this effect.
    const onPageShow = (event) => {
      if (event.persisted) {
        withdrawAiCallTabClosedIfRefreshed();
      }
    };

    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, []);
}
